<template>
	<div class="bottom-cashmatic-payment">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="handleToggle">
			<div class="bottom-cashmatic-content" v-if="active">
				<ModalHeader
					no-new-line
					:title="getString('cashmatic')"
					:button-text="headerButtonText"
					button-outline
					@click-button="headerButtonClick"
				/>
				<div class="container-payment-status" :class="{ 'has-warning': hasWarning }">
					<div class="column-status">
						<div class="column-status-name">{{ getString('to_pay') }}</div>
						<div class="column-status-value">{{ currencyStore.readableCurrencyValue(lastToPayValue) }}</div>
					</div>
					<div class="alignment-cta">
						<div class="column-status status-topay">
							<div class="column-status-name">{{ getString('inserted') }}</div>
							<div class="column-status-value">{{ currencyStore.readableCurrencyValue(displayInsertedAmount) }}</div>
						</div>
						<div class="column-status" :class="paymentCompleted ? 'status-change' : 'status-paid'">
							<div class="column-status-name">{{ paymentCompleted ? getString('change') : getString('remaining') }}</div>
							<div class="column-status-value">{{ currencyStore.readableCurrencyValue(paymentCompleted ? changeAmount : remainingAmount) }}</div>
						</div>
					</div>
				</div>
				<!-- Warning for not dispensed -->
				<div class="warning-container mt-20" v-if="hasWarning">
					<div class="warning-box">
						<p class="warning-title">{{ getString('warning') }}</p>
						<p class="warning-text">{{ getString('cashmatic_not_dispensed', [currencyStore.readableCurrencyValue(notDispensedAmount)]) }}</p>
					</div>
				</div>
				<!-- Progress bar -->
				<div class="progress-container mt-20" v-if="!paymentCompleted && !paymentError">
					<div class="progress-bar">
						<div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
					</div>
					<p class="progress-text mt-10">{{ progressText }}</p>
				</div>
				<!-- Loading state -->
				<div class="loader-container" v-if="!paymentCompleted && !paymentError">
					<AppLoader :size="40" />
					<p class="waiting-text">{{ getString('waiting_for_cash') }}</p>
				</div>
				<!-- Success state -->
				<div class="success-container mt-20" v-if="paymentCompleted && !hasWarning">
					<p class="success-text">{{ getString('payment_completed') }}</p>
				</div>
				<!-- Error state -->
				<div class="error-container mt-20" v-if="paymentError">
					<!-- Cancel succeeded: show success message + OK -->
					<template v-if="operationCancelledSuccess">
						<p class="success-text">{{ getString('cashmatic_operation_cancelled') }}</p>
						<AppButton class="mt-10" :text="getString('ok')" @click="closeOnError" />
					</template>
					<!-- Cancellable operation: show cancel button -->
					<template v-else-if="activeOperation && canCancelActiveOperation">
						<p class="error-text">{{ busyErrorText || paymentError }}</p>
						<p class="error-hint mt-10">{{ getString('cashmatic_cancel_operation_hint') }}</p>
						<AppButton class="mt-10" :text="getString('cancel_operation')" :loading="cancellingActiveOperation" @click="cancelActiveOperation" />
					</template>
					<!-- Non-cancellable or non-busy error: show OK -->
					<template v-else>
						<p class="error-text">{{ busyErrorText || paymentError }}</p>
						<AppButton class="mt-10" :text="getString('ok')" @click="closeOnError" />
					</template>
				</div>
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import {useSunmiStore} from "@/console-sun/stores";
import {getNative, isAndroid} from "@/shared/helpers/webapp";

export default {
	setup() {
		let languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const sunmiStore = useSunmiStore();
		return {getString: languageStore.getString, currencyStore, sunmiStore};
	},
	name: "BottomCashmaticPayment",
	components: {AppButton, AppLoader, ModalHeader, AppBottomSheet},
	emits: ["toggling-popup", "payment-done"],
	mixins: [WindowWidth],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		toPay: {
			type: Number,
			required: true,
		}
	},
	data() {
		return {
			cancelling: false,
			cancellingActiveOperation: false,
			operationCancelledSuccess: false,
			paymentError: null,
			paymentCompleted: false,
			finalResult: null,
			lastToPayValue: null,
		}
	},
	computed: {
		headerButtonText() {
			if(this.paymentCompleted || this.paymentError) {
				return '';
			}
			return this.getString('cancel');
		},
		bottomSheetWidth() {
			if(this.windowWidth < 600) {
				return "100%";
			} else {
				return "600px";
			}
		},
		insertedAmount() {
			return this.sunmiStore.cashmaticPaymentStatus?.inserted || 0;
		},
		displayInsertedAmount() {
			if(this.finalResult) {
				return this.finalResult.inserted || 0;
			}
			return this.insertedAmount;
		},
		remainingAmount() {
			const remaining = this.lastToPayValue - this.insertedAmount;
			return remaining > 0 ? remaining : 0;
		},
		changeAmount() {
			if(this.finalResult) {
				return this.finalResult.dispensed || 0;
			}
			return 0;
		},
		notDispensedAmount() {
			if(this.finalResult) {
				return this.finalResult.notDispensed || 0;
			}
			return 0;
		},
		hasWarning() {
			return this.paymentCompleted && this.notDispensedAmount > 0;
		},
		progressPercentage() {
			if(this.lastToPayValue <= 0) return 0;
			const percentage = (this.insertedAmount / this.lastToPayValue) * 100;
			return Math.min(percentage, 100);
		},
		progressText() {
			const status = this.sunmiStore.cashmaticPaymentStatus?.status;
			if(status) {
				const key = 'cashmatic_status_' + status;
				const translated = this.getString(key);
				return translated !== key ? translated : status;
			}
			return '';
		},
		activeOperation() {
			return this.sunmiStore.cashmaticActiveOperation;
		},
		canCancelActiveOperation() {
			return ['payment', 'refill', 'deposit'].includes(this.activeOperation);
		},
		busyErrorText() {
			if(!this.activeOperation) return null;
			const key = 'cashmatic_busy_' + this.activeOperation;
			const translated = this.getString(key);
			return translated !== key ? translated : this.getString('cashmatic_busy_generic');
		}
	},
	methods: {
		headerButtonClick() {
			if(this.paymentCompleted) {
				this.confirmAndClose();
			} else {
				this.cancelPayment();
			}
		},
		startPayment() {
			this.paymentError = null;
			this.paymentCompleted = false;
			this.finalResult = null;
			this.sunmiStore.resetCashmaticPayment();
			if(isAndroid()) {
				getNative().startCashmaticPayment(this.toPay, null);
			}
		},
		cancelPayment() {
			this.cancelling = true;
			if(isAndroid()) {
				getNative().cancelCashmaticPayment();
			}
		},
		cancelActiveOperation() {
			this.cancellingActiveOperation = true;
			this.sunmiStore.cashmaticPaymentState = null;
			if(isAndroid()) {
				if(this.activeOperation === 'payment') {
					getNative().cancelCashmaticPayment();
				} else if(this.activeOperation === 'refill') {
					getNative().stopCashmaticRefill();
				} else if(this.activeOperation === 'deposit') {
					getNative().stopCashmaticDeposit();
				}
			}
		},
		closeOnError() {
			this.$emit('toggling-popup', false);
			this.reset();
		},
		confirmAndClose() {
			const insertedAmount = this.finalResult?.inserted || this.insertedAmount;
			this.$emit('toggling-popup', false);
			this.$emit('payment-done', insertedAmount);
			this.reset();
		},
		handleToggle(isOpen) {
			if(!isOpen) {
				if(this.paymentCompleted) {
					this.sunmiStore.cashmaticPendingDismissal = false;
				} else if(this.sunmiStore.cashmaticPaymentState === 'started') {
					this.cancelPayment();
				}
				this.$emit('toggling-popup', false);
			}
		},
		reset() {
			this.cancelling = false;
			this.cancellingActiveOperation = false;
			this.operationCancelledSuccess = false;
			this.paymentError = null;
			this.paymentCompleted = false;
			this.finalResult = null;
			this.lastToPayValue = null;
			this.sunmiStore.resetCashmaticPayment();
		}
	},
	watch: {
		toPay: {
			immediate: true,
			handler(val) {
				if(!this.paymentCompleted) {
					this.lastToPayValue = val;
				}
			}
		},
		active(newVal) {
			if(newVal) {
				this.lastToPayValue = this.toPay;
				if(this.toPay <= 0) {
					// Zero payment - skip Cashmatic, directly complete
					this.$emit('toggling-popup', false);
					this.$emit('payment-done', 0);
					return;
				}
				this.startPayment();
			} else {
				this.reset();
			}
		},
		"sunmiStore.cashmaticPaymentState"(state) {
			if(state === 'completed' || state === 'committed') {
				this.finalResult = this.sunmiStore.cashmaticPaymentResult;
				this.paymentCompleted = true;
				this.sunmiStore.cashmaticPendingDismissal = true;
				this.$emit('payment-done', this.finalResult?.inserted || this.insertedAmount);
			} else if(state === 'cancelled') {
				this.cancelling = false;
				if(this.cancellingActiveOperation) {
					this.cancellingActiveOperation = false;
					this.operationCancelledSuccess = true;
					return;
				}
				if(!this.paymentError) {
					this.$emit('toggling-popup', false);
					this.reset();
				}
			} else if(state === 'error') {
				this.cancelling = false;
				this.cancellingActiveOperation = false;
				this.paymentError = this.sunmiStore.cashmaticPaymentError || this.getString('cashmatic_payment_error');
			}
		}
	}
}
</script>

<style scoped lang="scss">
.container-payment-status {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top: 10px;
	gap: 5px;

	&.has-warning {
		.column-status.status-change {
			background-color: var(--color-warning-bg, rgba(255, 193, 7, 0.2));
		}
	}

	.alignment-cta {
		display: flex;
		width: 66.666%;
		gap: 5px;
		@media (max-width: 520px) {
			flex-direction: column;
			width: calc(100% / 2);
			min-height: 165px;
		}
		@media (max-width: 450px) {
			min-height: 108px;
		}
	}
	.column-status {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		width: calc(100% / 3);
		min-height: 80px;
		padding: 10px;
		box-shadow: inset 0 1px 2px var(--color-box-shadow-3);
		border-radius: 13px;
		overflow: hidden;
		background-color: var(--color-bg);
		@media (max-width: 520px) {
			width: calc(100% / 2);
			min-height: 165px;
		}
		@media (max-width: 450px) {
			min-height: 110px;
			padding: 5px 10px;
		}

		&-name {
			font-size: 14px;
		}

		&-value {
			font-size: 24px;
			font-weight: 500;
			margin-top: 4px;
		}

		&.status-paid {
			background-color: var(--color-display-alpha-red);
			width: calc(100% / 2);
			@media (max-width: 520px) {
				width: 100%;
				min-height: 80px;
			}
			@media (max-width: 450px) {
				min-height: 50px;
			}
		}

		&.status-topay {
			background-color: var(--color-display-alpha-green);
			width: calc(100% / 2);
			@media (max-width: 520px) {
				width: 100%;
				min-height: 80px;
			}
			@media (max-width: 450px) {
				min-height: 50px;
			}
		}

		&.status-change {
			background-color: var(--color-bg-primary-2);
			width: calc(100% / 2);
			@media (max-width: 520px) {
				width: 100%;
				min-height: 80px;
			}
			@media (max-width: 450px) {
				min-height: 50px;
			}
		}

		.column-status-name {
			@include truncateString(1);
			width: 100%;
			text-align: center;
		}

		.column-status-value {
			@include truncateString(1);
			width: 100%;
			text-align: center;

			@media (max-width: 450px) {
				font-size: 18px;
			}
		}
	}
}

.progress-container {
	.progress-bar {
		width: 100%;
		height: 8px;
		background-color: var(--color-bg-2);
		border-radius: 4px;
		overflow: hidden;

		.progress-fill {
			height: 100%;
			background-color: var(--color-primary);
			border-radius: 4px;
			transition: width 0.3s ease;
		}
	}

	.progress-text {
		text-align: center;
		font-size: 14px;
		color: var(--color-txt-2);
	}
}

.loader-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 20px;
	min-height: 100px;

	.waiting-text {
		font-size: 16px;
		color: var(--color-txt-2);
	}
}

.success-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 20px;

	.success-text {
		font-size: 16px;
		color: var(--color-positive, #28a745);
		font-weight: 500;
	}
}

.warning-container {
	.warning-box {
		background-color: var(--color-warning-bg, rgba(255, 193, 7, 0.15));
		border: 1px solid var(--color-warning, #ffc107);
		border-radius: 13px;
		padding: 15px;
		text-align: center;

		.warning-title {
			font-size: 16px;
			font-weight: 600;
			color: var(--color-warning-text, #856404);
			margin-bottom: 8px;
		}

		.warning-text {
			font-size: 14px;
			color: var(--color-warning-text, #856404);
		}
	}
}

.error-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 20px;

	.error-text {
		font-size: 16px;
		color: var(--color-negative);
		text-align: center;
	}

	.error-hint {
		font-size: 14px;
		color: var(--color-txt-2);
		text-align: center;
	}
}
</style>
